import { Events, Variables, type WorkspaceSvg } from 'blockly/core';

import {
  SKYROVER_VARIABLE_TYPES,
  getSelectedVariableCreatorType,
  setSelectedVariableCreatorType,
} from './variableTypes.js';

const VARIABLES_CATEGORY_NAME = 'Variables';
const FLYOUT_KEEP_OPEN_MS = 1500;
const VARIABLES_PANEL_MIN_WIDTH_PX = 240;

function stopWorkspaceBubble(event: Event): void {
  event.stopPropagation();
}

export function attachSkyroverVariableCreatorBar(
  container: HTMLElement,
  workspace: WorkspaceSvg,
): () => void {
  let panelEl: HTMLDivElement | null = null;
  let headerEl: HTMLDivElement | null = null;
  let typeSelect: HTMLSelectElement | null = null;
  let keepFlyoutOpenUntil = 0;

  const findInjectionDiv = (): HTMLElement => container.querySelector('.injectionDiv') ?? container;

  const findFlyout = (): SVGSVGElement | null =>
    container.querySelector('svg.blocklyToolboxFlyout, svg.blocklyFlyout');

  const markKeepFlyoutOpen = (): void => {
    keepFlyoutOpenUntil = Date.now() + FLYOUT_KEEP_OPEN_MS;
  };

  const shouldKeepFlyoutOpen = (): boolean => Date.now() < keepFlyoutOpenUntil;

  const isFlyoutVisible = (): boolean => {
    const flyout = findFlyout();
    if (!flyout) {
      return false;
    }

    const style = window.getComputedStyle(flyout);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }

    const rect = flyout.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const isVariablesCategorySelected = (): boolean => {
    const toolbox = workspace.getToolbox();
    const selectedItem = toolbox?.getSelectedItem() as { getName?: () => string } | null | undefined;
    return selectedItem?.getName?.() === VARIABLES_CATEGORY_NAME;
  };

  const preserveVariablesFlyout = (): void => {
    const toolbox = workspace.getToolbox();
    if (!toolbox || isVariablesCategorySelected()) {
      syncPanelPosition();
      return;
    }

    for (const item of toolbox.getToolboxItems()) {
      const selectable = item as {
        getName?: () => string;
        isSelectable?: () => boolean;
      };
      if (selectable.isSelectable?.() && selectable.getName?.() === VARIABLES_CATEGORY_NAME) {
        toolbox.setSelectedItem(item);
        break;
      }
    }

    workspace.refreshToolboxSelection();
    syncPanelPosition();
  };

  const syncPanelPosition = (): void => {
    if (!panelEl || panelEl.hidden) {
      return;
    }

    const flyout = findFlyout();
    if (!flyout || !isFlyoutVisible()) {
      panelEl.hidden = true;
      return;
    }

    const flyoutRect = flyout.getBoundingClientRect();
    const injectionRect = findInjectionDiv().getBoundingClientRect();
    const panelTop = flyoutRect.top;
    const hasVariables = workspace.getVariableMap().getAllVariables().length > 0;
    const fullPanelHeight = Math.max(injectionRect.bottom - panelTop, flyoutRect.height);
    const headerHeight = headerEl?.offsetHeight ?? 42;

    panelEl.style.left = `${flyoutRect.left}px`;
    panelEl.style.top = `${panelTop}px`;
    panelEl.style.width = `${Math.max(flyoutRect.width, VARIABLES_PANEL_MIN_WIDTH_PX)}px`;
    panelEl.style.height = `${Math.max(hasVariables ? headerHeight : fullPanelHeight, 0)}px`;
    panelEl.classList.toggle('skyrover-variable-creator-panel--empty', !hasVariables);
  };

  const attachBarBubbleGuard = (element: HTMLElement): void => {
    for (const eventName of ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click'] as const) {
      element.addEventListener(eventName, stopWorkspaceBubble);
    }
  };

  const createVariable = (): void => {
    markKeepFlyoutOpen();

    const type = typeSelect?.value ?? getSelectedVariableCreatorType();
    setSelectedVariableCreatorType(type);
    Variables.createVariableButtonHandler(
      workspace,
      () => {
        markKeepFlyoutOpen();
        requestAnimationFrame(() => {
          preserveVariablesFlyout();
          workspace.refreshToolboxSelection();
        });
      },
      type,
    );
  };

  const ensurePanel = (): void => {
    if (panelEl) {
      return;
    }

    panelEl = document.createElement('div');
    panelEl.className = 'skyrover-variable-creator-panel';
    panelEl.hidden = true;
    attachBarBubbleGuard(panelEl);
    panelEl.addEventListener('pointerdown', markKeepFlyoutOpen);

    headerEl = document.createElement('div');
    headerEl.className = 'skyrover-variable-creator';

    const label = document.createElement('span');
    label.className = 'skyrover-variable-creator__label';
    label.textContent = 'Create variable';

    typeSelect = document.createElement('select');
    typeSelect.className = 'skyrover-variable-creator__type';
    typeSelect.setAttribute('aria-label', 'Variable type');
    for (const entry of SKYROVER_VARIABLE_TYPES) {
      const option = document.createElement('option');
      option.value = entry.type;
      option.textContent = entry.label;
      typeSelect.appendChild(option);
    }
    typeSelect.value = getSelectedVariableCreatorType();
    typeSelect.addEventListener('change', () => {
      setSelectedVariableCreatorType(typeSelect?.value ?? 'float');
      markKeepFlyoutOpen();
      preserveVariablesFlyout();
    });
    typeSelect.addEventListener('mousedown', () => {
      markKeepFlyoutOpen();
    });
    typeSelect.addEventListener('focus', () => {
      markKeepFlyoutOpen();
      requestAnimationFrame(preserveVariablesFlyout);
    });

    const createButton = document.createElement('button');
    createButton.type = 'button';
    createButton.className = 'skyrover-variable-creator__button';
    createButton.setAttribute('aria-label', 'Create variable');
    createButton.title = 'Create variable';
    createButton.textContent = '+';
    createButton.addEventListener('mousedown', (event) => {
      event.preventDefault();
      markKeepFlyoutOpen();
    });
    createButton.addEventListener('click', (event) => {
      event.stopPropagation();
      createVariable();
    });

    headerEl.append(label, typeSelect, createButton);
    panelEl.append(headerEl);
    document.body.appendChild(panelEl);
  };

  const setVisible = (visible: boolean): void => {
    ensurePanel();
    if (!panelEl) {
      return;
    }

    const shouldShow = visible && isVariablesCategorySelected() && isFlyoutVisible();
    panelEl.hidden = !shouldShow;
    if (shouldShow) {
      requestAnimationFrame(syncPanelPosition);
    }
  };

  const refreshVisibility = (): void => {
    setVisible(isVariablesCategorySelected());
  };

  const listener = (event: Events.Abstract): void => {
    if (event.type === Events.TOOLBOX_ITEM_SELECT) {
      const selectEvent = event as Events.ToolboxItemSelect;

      if (!selectEvent.newItem && shouldKeepFlyoutOpen()) {
        requestAnimationFrame(preserveVariablesFlyout);
        return;
      }

      setVisible(selectEvent.newItem === VARIABLES_CATEGORY_NAME);
      return;
    }

    if (event.type === Events.VAR_CREATE || event.type === Events.VAR_DELETE) {
      markKeepFlyoutOpen();
      requestAnimationFrame(() => {
        preserveVariablesFlyout();
        syncPanelPosition();
      });
      return;
    }

    if (event.type === Events.VIEWPORT_CHANGE) {
      syncPanelPosition();
    }
  };

  workspace.addChangeListener(listener);

  const domObserver = new MutationObserver(() => {
    refreshVisibility();
    syncPanelPosition();
  });
  domObserver.observe(container, { childList: true, subtree: true });

  const resizeObserver = new ResizeObserver(() => {
    syncPanelPosition();
  });

  const onWindowChange = (): void => {
    refreshVisibility();
    syncPanelPosition();
  };

  window.addEventListener('resize', onWindowChange);
  window.addEventListener('scroll', onWindowChange, true);

  ensurePanel();
  resizeObserver.observe(findInjectionDiv());
  const flyout = findFlyout();
  if (flyout) {
    resizeObserver.observe(flyout);
  }

  refreshVisibility();

  return () => {
    workspace.removeChangeListener(listener);
    domObserver.disconnect();
    resizeObserver.disconnect();
    window.removeEventListener('resize', onWindowChange);
    window.removeEventListener('scroll', onWindowChange, true);
    panelEl?.remove();
    panelEl = null;
    headerEl = null;
    typeSelect = null;
  };
}
